import { useEffect, useState } from 'react';
import type { NetworkResource, Relationship } from '@shared/types';
import { generateDeepLink } from '@shared/deep-links';
import { getResource, getResourceRelationships } from '../lib/api-client';
import { cn } from '../lib/utils';
import { X, ChevronLeft, ExternalLink, Loader2, AlertTriangle, Network, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

interface ResourceModalProps {
  resourceId: string;
  onClose: () => void;
}

export function ResourceModal({ resourceId, onClose }: ResourceModalProps) {
  const [currentId, setCurrentId] = useState(resourceId);
  const [history, setHistory] = useState<string[]>([]);
  const [resource, setResource] = useState<NetworkResource | null>(null);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [resolvedUrlMapName, setResolvedUrlMapName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      setResolvedUrlMapName(null);
      try {
        const [resResource, resRelationships] = await Promise.all([
          getResource(currentId),
          getResourceRelationships(currentId).catch(() => ({ data: [], stale: false, cachedAt: null })),
        ]);
        setResource(resResource.data);
        const rels = Array.isArray(resRelationships.data) ? resRelationships.data : [];
        setRelationships(rels);

        // For global LBs without naming pattern, resolve url_map via target proxy chain
        const res = resResource.data;
        if (res && res.provider === 'gcp' && res.resourceType === 'load_balancer' && !res.region) {
          const deepLink = generateDeepLink(res);
          if (deepLink && deepLink.includes('/loadbalancing/list/loadBalancers')) {
            // Find target proxy in relationships
            const proxyRel = rels.find(r => {
              const isSource = r.sourceResourceId === currentId;
              const targetType = isSource ? r.targetResourceType : r.sourceResourceType;
              return targetType === 'target_proxy';
            });
            if (proxyRel && proxyRel.targetResourceId) {
              try {
                const proxyRels = await getResourceRelationships(proxyRel.targetResourceId);
                const proxyRelData = Array.isArray(proxyRels.data) ? proxyRels.data : [];
                const urlMapRel = proxyRelData.find(r => {
                  const isSource = r.sourceResourceId === proxyRel.targetResourceId;
                  const targetType = isSource ? r.targetResourceType : r.sourceResourceType;
                  return targetType === 'url_map';
                });
                if (urlMapRel) {
                  const isSource = urlMapRel.sourceResourceId === proxyRel.targetResourceId;
                  const urlMapName = isSource ? urlMapRel.targetName : urlMapRel.sourceName;
                  if (urlMapName) setResolvedUrlMapName(urlMapName);
                }
              } catch (_e) { /* ignore - best effort */ }
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error loading resource');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [currentId]);

  // Navigate to a related resource
  const navigateToResource = (targetId: string) => {
    setHistory((prev) => [...prev, currentId]);
    setCurrentId(targetId);
  };

  // Go back to previous resource
  const goBack = () => {
    const prev = history[history.length - 1];
    if (prev) {
      setHistory((h) => h.slice(0, -1));
      setCurrentId(prev);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl animate-in fade-in-0 zoom-in-95">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white/95 backdrop-blur-sm px-6 py-4">
          <div className="flex items-center gap-3">
            {history.length > 0 && (
              <button
                onClick={goBack}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Go back"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div>
              {resource ? (
                <>
                  <h2 className="text-lg font-bold text-gray-900">{resource.name}</h2>
                  <div className="mt-0.5 flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px] uppercase">{resource.provider}</Badge>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{resource.resourceType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{resource.region ?? 'Global'}</span>
                  </div>
                </>
              ) : (
                <h2 className="text-lg font-bold text-gray-900">Resource Detail</h2>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto max-h-[calc(85vh-73px)] p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <p className="text-sm text-gray-400">Cargando recurso…</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {resource && !loading && (
            <>
              {/* Deep Link Button */}
              {(() => {
                // For LB-related resources, always compute fresh (stored URLs may be outdated)
                const alwaysRecompute = ['load_balancer', 'target_proxy', 'backend_service', 'url_map', 'dns_policy', 'vpn_gateway', 'nat_gateway', 'route_table', 'target_group', 'psc_service_attachment', 'peering', 'partner_interconnect', 'ncc_hub', 'ncc_spoke', 'service_networking_connection'].includes(resource.resourceType);
                const storedUrl = resource.consoleUrl;
                const isBrokenUrl = storedUrl && (
                  storedUrl.includes('/loadbalancing/proxies/') ||
                  storedUrl.includes('/loadbalancing/details/{') ||
                  storedUrl.includes('/loadbalancing/backends/')
                );
                let consoleUrl = (alwaysRecompute || !storedUrl || isBrokenUrl) ? generateDeepLink(resource) : storedUrl;
                
                // For global LBs that fell back to list, try to resolve via relationships
                if (consoleUrl && consoleUrl.includes('/loadbalancing/list/loadBalancers') && resource.resourceType === 'load_balancer' && !resource.region) {
                  // Use resolved url_map name from the proxy chain lookup
                  if (resolvedUrlMapName) {
                    consoleUrl = `https://console.cloud.google.com/net-services/loadbalancing/details/httpAdvanced/${encodeURIComponent(resolvedUrlMapName)}?project=${encodeURIComponent(resource.projectOrAccount)}`;
                  } else {
                    // Check if there's a url_map directly in relationships
                    const urlMapRel = relationships.find(r => {
                      const isSource = r.sourceResourceId === currentId;
                      const targetType = isSource ? r.targetResourceType : r.sourceResourceType;
                      return targetType === 'url_map';
                    });
                    if (urlMapRel) {
                      const isSource = urlMapRel.sourceResourceId === currentId;
                      const urlMapName = isSource ? urlMapRel.targetName : urlMapRel.sourceName;
                      if (urlMapName) {
                        consoleUrl = `https://console.cloud.google.com/net-services/loadbalancing/details/httpAdvanced/${encodeURIComponent(urlMapName)}?project=${encodeURIComponent(resource.projectOrAccount)}`;
                      }
                    }
                  }
                }
                
                if (consoleUrl) {
                  return (
                    <Button asChild variant="primary" size="sm" className="gap-2">
                      <a href={consoleUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open in {resource.provider === 'gcp' ? 'GCP Console' : 'AWS Console'}
                      </a>
                    </Button>
                  );
                }
                return (
                  <Button variant="outline" size="sm" disabled className="gap-2 cursor-not-allowed">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Console link not available
                  </Button>
                );
              })()}

              {/* Properties */}
              <section>
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
                  <div className="h-px flex-1 bg-gray-100" />
                  Properties
                  <div className="h-px flex-1 bg-gray-100" />
                </h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PropItem label="Provider" value={resource.provider.toUpperCase()} />
                  <PropItem label="Type" value={resource.resourceType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} />
                  <PropItem label="Region" value={resource.region ?? 'Global'} />
                  <PropItem label="Project / Account" value={resource.projectOrAccount} />
                  <PropItem label="Status" value={resource.status} />
                  <PropItem label="Native ID" value={resource.nativeId} mono />
                  <PropItem label="Discovered" value={new Date(resource.discoveredAt).toLocaleString()} />
                  <PropItem label="Last Updated" value={new Date(resource.updatedAt).toLocaleString()} />
                </dl>
              </section>

              {/* Relationships */}
              <section>
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
                  <div className="h-px flex-1 bg-gray-100" />
                  Relationships ({relationships.length})
                  <div className="h-px flex-1 bg-gray-100" />
                </h3>
                {relationships.length === 0 ? (
                  <p className="text-sm text-gray-500">No relationships found</p>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {groupRelationshipsByType(relationships, currentId).map(({ type, label, items }) => (
                      <div key={type} className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                        <div className="bg-gray-50/80 px-4 py-2.5 border-b border-gray-100">
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            {label} <span className="text-gray-400 font-normal">({items.length})</span>
                          </h4>
                        </div>
                        <div className="divide-y divide-gray-50 max-h-48 overflow-y-auto">
                          {items.map((item) => (
                            <div
                              key={item.rel.id}
                              className={cn(
                                'flex items-center justify-between px-4 py-2.5 text-sm transition-colors',
                                item.isClickable && 'cursor-pointer hover:bg-primary-50/50 group'
                              )}
                              onClick={() => item.isClickable && item.targetId && navigateToResource(item.targetId)}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-gray-900 truncate font-medium text-sm">
                                  {item.name || '(unknown)'}
                                </span>
                              </div>
                              <span className="shrink-0 ml-2">
                                {item.rel.targetNotDiscovered ? (
                                  <Badge variant="secondary" className="text-[9px]">external</Badge>
                                ) : item.isClickable ? (
                                  <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-primary-500 transition-colors" />
                                ) : null}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Key Metadata */}
              {Object.keys(resource.metadata).length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
                    <div className="h-px flex-1 bg-gray-100" />
                    Metadata
                    <div className="h-px flex-1 bg-gray-100" />
                  </h3>
                  <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap break-all font-mono leading-relaxed">
                      {JSON.stringify(resource.metadata, null, 2)}
                    </pre>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Groups relationships by target resource type and provides display info.
 */
function groupRelationshipsByType(
  relationships: Relationship[],
  currentId: string
): Array<{ type: string; label: string; items: Array<{ rel: Relationship; targetId: string | null; name: string; isClickable: boolean }> }> {
  const groups = new Map<string, Array<{ rel: Relationship; targetId: string | null; name: string; isClickable: boolean }>>();

  for (const rel of relationships) {
    // Determine which side is "the other resource"
    const isSource = rel.sourceResourceId === currentId;
    const targetId = isSource ? rel.targetResourceId : rel.sourceResourceId;
    let resourceType = isSource
      ? (rel.targetResourceType || null)
      : (rel.sourceResourceType || null);
    let name = isSource
      ? (rel.targetName || rel.targetNativeId || 'Unknown')
      : (rel.sourceName || 'Unknown');
    const isClickable = !!targetId && !rel.targetNotDiscovered;

    // For not-discovered targets, extract a friendly name from the selfLink
    if (rel.targetNotDiscovered && rel.targetNativeId) {
      const nativeId = rel.targetNativeId;
      // Extract just the resource name from the selfLink
      const resourceName = nativeId.split('/').pop() || nativeId;
      // Check if it's a Google-managed project
      const googleManagedPatterns = [
        /\/projects\/(speckle-umbrella|cloud-sql|cloud-memorystore|gke-mcs|cloud-redis|servicenetworking|cloudcomposer)/,
        /\/projects\/[a-z]+-tp[0-9]+/,
      ];
      const isGoogleManaged = googleManagedPatterns.some(p => p.test(nativeId));
      if (isGoogleManaged) {
        name = `${resourceName} (Google-managed)`;
      } else {
        // Check if it's an external project (different from current resource's project)
        const projectMatch = nativeId.match(/\/projects\/([^/]+)/);
        const externalProject = projectMatch?.[1];
        if (externalProject) {
          name = `${resourceName} (${externalProject})`;
        } else {
          name = resourceName;
        }
      }
    }

    // Infer resource type from targetNativeId (GCP selfLink) when not discovered
    if (!resourceType && rel.targetNativeId) {
      resourceType = inferResourceTypeFromSelfLink(rel.targetNativeId);
    }

    const groupKey = resourceType || 'unknown';
    const items = groups.get(groupKey) || [];
    items.push({ rel, targetId, name, isClickable });
    groups.set(groupKey, items);
  }

  // Sort groups alphabetically by type label
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([type, items]) => ({
      type,
      label: type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      items,
    }));
}

/**
 * Infers a resource type from a GCP selfLink URL.
 */
function inferResourceTypeFromSelfLink(selfLink: string): string | null {
  if (selfLink.includes('/targetVpnGateways/')) return 'vpn_gateway';
  if (selfLink.includes('/vpnGateways/')) return 'vpn_gateway';
  if (selfLink.includes('/vpnTunnels/')) return 'vpn_tunnel';
  if (selfLink.includes('/targetHttpProxies/')) return 'target_proxy';
  if (selfLink.includes('/targetHttpsProxies/')) return 'target_proxy';
  if (selfLink.includes('/targetTcpProxies/')) return 'target_proxy';
  if (selfLink.includes('/targetSslProxies/')) return 'target_proxy';
  if (selfLink.includes('/urlMaps/')) return 'url_map';
  if (selfLink.includes('/backendServices/')) return 'backend_service';
  if (selfLink.includes('/targetPools/')) return 'target_group';
  if (selfLink.includes('/forwardingRules/')) return 'load_balancer';
  if (selfLink.includes('/networks/')) return 'vpc';
  if (selfLink.includes('/subnetworks/')) return 'subnet';
  if (selfLink.includes('/routers/')) return 'cloud_router';
  if (selfLink.includes('/firewalls/')) return 'firewall_rule';
  if (selfLink.includes('/serviceAttachments/')) return 'psc_service_attachment';
  return null;
}

function PropItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-100 bg-gray-50/50 px-3.5 py-2.5">
      <dt className="text-[10px] font-medium uppercase tracking-wider text-gray-400">{label}</dt>
      <dd className={cn('mt-0.5 text-sm text-gray-900 break-all', mono && 'font-mono text-xs')}>
        {value}
      </dd>
    </div>
  );
}
